import { useEffect, useImperativeHandle, useState } from 'react';
import { Box, HStack, Icon, Input, Stack, Text } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Field } from '@/components/ui/field';
import { InputGroup } from '@/components/ui/input-group';
import { SimpleSelect } from '@/components/ui/simple-select';
import TextEditor from '@/components/ui/text-editor';
import { PlanDraft } from '@/pages/planner/draft';

const DURATION_OPTIONS = [45, 60, 75, 90, 120];
const CUSTOM_DURATION = 'custom';

const DURATION_ITEMS = [
  ...DURATION_OPTIONS.map((d) => ({ label: `${d} min`, value: String(d) })),
  { label: 'Custom…', value: CUSTOM_DURATION },
];

// start_time has no place in the CurriculumPlan wire contract (slots aren't
// scheduled at a time of day — only the day plan copied from a slot is), so
// it's only required when the form isn't in curriculum mode. See
// buildPlanInfoSchema below.
function buildPlanInfoSchema(isCurriculumMode: boolean) {
  return z.object({
    name: z.string().min(1, 'Plan title is required'),
    start_time: isCurriculumMode ? z.string() : z.string().min(1, 'Start time is required'),
    duration_minutes: z.coerce.number().int('Use whole minutes').positive('Duration is required'),
    goals: z.string(),
    notes: z.string(),
  });
}

export type PlanInfoFormValues = z.infer<ReturnType<typeof buildPlanInfoSchema>>;

// Imperative handle so builder-screen can gate Save on this form's validity
// without lifting full RHF state — the watch()-based onChange plumbing below
// remains the source of truth for draft values; this only exposes validation.
export interface PlanInfoFormHandle {
  validate: () => Promise<boolean>;
}

interface PlanInfoFormProps {
  draft: PlanDraft;
  isCurriculumMode: boolean;
  onChange: (values: PlanInfoFormValues) => void;
  formRef?: React.Ref<PlanInfoFormHandle>;
}

// Read-only display of a value inherited from the parent curriculum. The eyebrow
// names the field (two of these stack, so a generic "FROM CURRICULUM" label left
// them indistinguishable) and the lock icon signals it isn't editable here.
// `hint` explains where it *is* editable — shown in curriculum mode, where the
// admin is one click away from the curriculum's own Edit details dialog.
function LockedBox({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Box bg='bg.subtle' borderRadius='md' p={3}>
      <Text textStyle='eyebrow' mb={1} display='flex' alignItems='center' gap={1}>
        <Icon as={LockClosedIcon} w={3} h={3} />
        {label} · from curriculum
      </Text>
      <Text fontSize='sm' color='fg' whiteSpace='pre-wrap'>
        {value}
      </Text>
      {hint && (
        <Text fontSize='xs' color='fg.subtle' mt={1.5}>
          {hint}
        </Text>
      )}
    </Box>
  );
}

// "1. Plan information" — left column of the builder. RHF+zod for the
// scalar fields; sections live in builder-screen's useState and are wired
// separately. Learning outcomes and prerequisites are never edited here: they
// belong to the curriculum as a whole, so both modes render them read-only —
// curriculum mode so the admin can see what the curriculum declares while
// building a slot, day mode (outcomes_locked) because the plan snapshotted them
// from the curriculum at copy time. A day plan still saves its snapshot; a slot
// save simply omits them (see builder-screen's performSave).
export default function PlanInfoForm({ draft, isCurriculumMode, onChange, formRef }: PlanInfoFormProps) {
  const { register, watch, trigger, setValue, formState: { errors } } = useForm<PlanInfoFormValues>({
    resolver: zodResolver(buildPlanInfoSchema(isCurriculumMode)),
    mode: 'onChange',
    defaultValues: {
      name: draft.name,
      start_time: draft.start_time,
      duration_minutes: draft.duration_minutes,
      goals: draft.goals,
      notes: draft.notes,
    },
  });

  // Custom mode is sticky UI state, not derived from the value — a typed 90
  // must not snap the input back into the "90 min" preset mid-edit. Drafts
  // loaded with an off-preset duration (e.g. a copied 50-min plan) open in
  // custom mode so their value stays visible and editable.
  const [isCustomDuration, setIsCustomDuration] = useState(
    () => !DURATION_OPTIONS.includes(draft.duration_minutes),
  );
  const durationValue = watch('duration_minutes');

  // Exposed to builder-screen so Save can gate on validity: trigger()
  // re-validates every field, flips formState.errors so the inline messages
  // below render, and resolves false if anything (e.g. a cleared start_time)
  // fails the zod schema.
  useImperativeHandle(formRef, () => ({
    validate: () => trigger(),
  }), [trigger]);

  // Push every keystroke up to the parent draft — the builder screen is the
  // source of truth for the combined draft (scalars + sections) since save
  // needs both together and there's no separate "submit" step for this form.
  // watch() returns raw (uncoerced) form state, so duration_minutes must be
  // coerced by hand here — the zod schema's z.coerce only runs through the
  // resolver on submit/validate, not on watch().
  useEffect(() => {
    const subscription = watch((values) => {
      onChange({
        ...(values as PlanInfoFormValues),
        duration_minutes: Number(values.duration_minutes) || 0,
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange]);

  // Both modes surface the inherited text; each box renders only when it has a
  // value, matching plan-body.tsx's read-only view.
  const showInherited = isCurriculumMode || draft.outcomes_locked;
  const inheritedHint = isCurriculumMode
    ? 'Set on the curriculum, not on this plan — use “Edit details” on the curriculum to change it.'
    : undefined;

  return (
    <Stack gap={4}>
      <Field label='Plan title' invalid={!!errors.name} errorText={errors.name?.message}>
        <Input {...register('name')} placeholder='Practice plan title' />
      </Field>

      <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
        {/* Curriculum plans (slots) have no start_time in the wire contract —
            only the day plan later copied from a slot gets one. */}
        {!isCurriculumMode && (
          <Field label='Start time' invalid={!!errors.start_time} errorText={errors.start_time?.message}>
            <Input {...register('start_time')} type='time' />
          </Field>
        )}
        <Field
          label='Duration'
          invalid={!!errors.duration_minutes}
          errorText={errors.duration_minutes?.message}
        >
          <HStack w='full' gap={2}>
            <SimpleSelect
              items={DURATION_ITEMS}
              value={isCustomDuration ? CUSTOM_DURATION : String(durationValue)}
              onValueChange={(selected) => {
                if (!selected) return;
                if (selected === CUSTOM_DURATION) {
                  setIsCustomDuration(true);
                } else {
                  setIsCustomDuration(false);
                  setValue('duration_minutes', Number(selected), { shouldValidate: true });
                }
              }}
              placeholder='Duration'
              flex='1'
            />
            {isCustomDuration && (
              <InputGroup endElement='min' maxW='110px'>
                <Input {...register('duration_minutes')} type='number' min={1} />
              </InputGroup>
            )}
          </HStack>
        </Field>
      </Stack>

      {/* CKEditor isn't a native input, so these two fields bypass register():
          the editor pushes HTML strings into form state via setValue, which
          still flows through the watch() subscription above. Values are HTML;
          plan-body.tsx renders them with Prose + DOMPurify. */}
      <Field label='Goals & objectives'>
        <TextEditor
          initialValue={draft.goals}
          placeholder='What should athletes take away from this session?'
          onChange={(html) => setValue('goals', html)}
        />
      </Field>

      <Field label='Notes'>
        <TextEditor
          initialValue={draft.notes}
          placeholder='Coach notes for this plan (optional)'
          onChange={(html) => setValue('notes', html)}
        />
      </Field>

      {showInherited && (
        <>
          {draft.learning_outcomes && (
            <LockedBox
              label='Learning outcomes'
              value={draft.learning_outcomes}
              hint={inheritedHint}
            />
          )}
          {draft.prerequisites && (
            <LockedBox label='Prerequisites' value={draft.prerequisites} hint={inheritedHint} />
          )}
        </>
      )}
    </Stack>
  );
}
